import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { tap, mergeMap } from 'rxjs/operators';
import { forkJoin as RxForkJoin } from 'rxjs/observable/forkJoin';
import { from as RxFrom } from 'rxjs/observable/from';
import { find as _find, isEmpty as _isEmpty } from 'lodash';

import { BlAppContext, BlClientIdManager } from '@blcloud/bl-ng-common';
import { UserLoginInfoResource } from '@blcloud/bl-ng-resource';
import { Rules } from '@blcloud/bl-ng-ui-component';
import { LocalStorageUtils } from '@blcloud/bl-common';

import {
  IKPASLogin,
  IKPASMachines,
  IKPASRegistMachine,
  IKPASUpdateMachineToken,
  IKPASFaceMatch,
  IKPASPersons,
  IKPASResponseBase,
  IKPASRegistImage,
  IKPASPerson
} from './kpas/kpas.interface';
import { KPASStatus } from './kpas/kpas.enum';
import { IFaceDetectionMockLogin } from './face-detection-modal.interface';

@Injectable()
export class FaceDetectionModalService {
  /** モック機能でローカルストレージに保持する際のキー */
  static readonly BACKUP_KEY = 'face-login';

  /** enviromentに定義している顔認証サービスの設定値 */
  private envFaceDetection;

  /** 顔認証サービスログイン情報 */
  private login: IKPASLogin;

  /** ドメインのパス */
  private domainPath: string;

  constructor(
    private appContext: BlAppContext,
    private clientIdManager: BlClientIdManager,
    private httpClient: HttpClient,
    private userLoginInfoResource: UserLoginInfoResource
  ) {
    this.envFaceDetection = this.appContext.environment.faceDetection;
    this.domainPath = this.appContext.environment.auth ? this.envFaceDetection.basePath : 'http://localhost:4200';
  }

  /**
   * 構成データを取得する
   */
  fetchConfig() {
    return RxForkJoin(this.loginService(), this.getRules());
  }

  /**
   * 照合端末一覧を検索する
   * * LocalStorageに保持しているCLIENT_DEVICE_IDを利用する
   * @param loginToken ログイン時に発行されたトークン
   */
  searchMachines(loginToken: string) {
    return this.post<IKPASMachines>(
      '/face-api/machine/list',
      {
        token: loginToken,
        machineName: this.clientIdManager.getClientDeviceId(),
        status: KPASStatus.Active,
      }
    );
  }

  /**
   * 照合端末を登録する
   * @param loginToken ログイン時に発行されたトークン
   */
  registMachine(loginToken: string) {
    return this.post<IKPASRegistMachine>(
      '/face-api/machine/create',
      {
        token: loginToken,
        machineName: this.clientIdManager.getClientDeviceId()
      }
    );
  }

  /**
   * 照合端末のトークンを更新する
   * @param loginToken ログイン時に発行されたトークン
   * @param machineId 端末ID
   */
  updateMachineToken(loginToken: string, machineId: string) {
    return this.post<IKPASUpdateMachineToken>(
      '/face-api/machine/updateToken',
      { token: loginToken, machineId }
    );
  }

  /**
   * 顔画像を照合する
   * @param machineId 端末ID
   * @param machineToken 照合端末トークン
   * @param image キャプチャ画像
   */
  faceMatch(machineId: string, machineToken: string, image: string) {
    return RxFrom(fetch(image).then(res => res.blob()).then(blob => blob)).pipe(
      mergeMap(
        fileBlob => {
          const formData = new FormData();
          formData.append('machineId', machineId);
          formData.append('token', machineToken);
          formData.append('image', fileBlob, 'face.jpeg');
          formData.append('minWidth', '50');
          formData.append('minHeight', '50');
          formData.append('maxWidth', '1980');
          formData.append('maxHeight', '1980');
          formData.append('score', '800');
          return this.post<IKPASFaceMatch>('/face-api/collation/matchImage', formData, true);
        }
      )
    );
  }

  /**
   * .CのログインIDに紐づくユーザーを検索する
   * @param loginToken ログイン時に発行されたトークン
   * @param blLoginId .cログインID
   */
  searchPerson(loginToken: string, blLoginId: string) {
    return this.post<IKPASPersons>(
      '/face-api/person/list',
      { token: loginToken, externalId: blLoginId, status: [KPASStatus.Active, KPASStatus.Interim] }
    );
  }

  /**
   * .cにユーザーに対応するユーザーを登録する
   * @param loginToken ログイン時に発行されたトークン
   * @param blLoginId .cログインID
   */
  registPerson(loginToken: string, blLoginId: string) {
    return this.post<IKPASResponseBase>(
      '/face-api/person/create',
      {
        token: loginToken,
        externalId: blLoginId
      }
    ).pipe(
      mergeMap(() => this.searchPerson(loginToken, blLoginId))
    );
  }

  /**
   * 顔画像を登録する
   * @param loginToken ログイン時に発行されたトークン
   * @param externalId 外部システムの顧客ID
   * @param image 顔画像
   */
  registImage(loginToken: string, externalId: string, image: string) {
    return RxFrom(fetch(image).then(res => res.blob()).then(blob => blob)).pipe(
      mergeMap(
        fileBlob => {
          const formData = new FormData();
          formData.append('token', loginToken);
          formData.append('externalId', externalId);
          formData.append('image', fileBlob, 'face.jpeg');
          return this.post<IKPASRegistImage>('/face-api/person/registerImage', formData, true);
        }
      )
    );
  }

  /**
   * ローカスストレージに顔認証用ログイン情報を保存する
   * @param loginId .CログインID
   * @param password .Cログインパスワード
   * @param person ユーザー情報
   */
  backupStorage(loginId: string, password: string, person: IKPASPerson) {
    const loginList = this.getMockLoginList();
    const mockLogin = this.getMockLogin(person, loginList);
    if (mockLogin) {
      // 更新する
      mockLogin.loginId = loginId;
      mockLogin.password = password;
      mockLogin.personId = person.personId;
      LocalStorageUtils.save(FaceDetectionModalService.BACKUP_KEY, loginList);

      return mockLogin;
    } else {
      const add = { loginId, password, personId: person.personId };
      LocalStorageUtils.save(FaceDetectionModalService.BACKUP_KEY, [...loginList, add]);
      return add;
    }
  }

  /**
   * ローカスストレージに保存済みの顔認証用ログイン情報一覧を取得する
   */
  getMockLoginList() {
    return LocalStorageUtils.get(FaceDetectionModalService.BACKUP_KEY) as IFaceDetectionMockLogin[] || [];
  }

  /**
   * ローカスストレージに保存済みの顔認証用ログイン情報一覧から利用者IDに一致するデータのみを取得する
   * @param person 利用者情報
   * @param list 抽出対象(顔認証用ログイン情報一覧)
   */
  getMockLogin(person: IKPASPerson, list?: IFaceDetectionMockLogin[]) {
    let loginList = list;
    if (_isEmpty(loginList)) {
      loginList = LocalStorageUtils.get(FaceDetectionModalService.BACKUP_KEY) as IFaceDetectionMockLogin[] || [];
    }
    return _find(loginList, login => login.personId === person.personId);
  }

  /**
   * 顔認証サービスAPI（POST）をコールする
   * @param path APIのパス
   * @param body 送信データ
   * @param isForm フォーム送信
   */
  private post<T>(path: string, body: any, isForm?: boolean) {
    return this.httpClient.post<T>(
      `${this.domainPath}${path}`,
      body,
      { headers: this.createHeaders(isForm) }
    );
  }

  /**
   * ルールを取得する
   */
  private getRules() {
    return this.userLoginInfoResource.getRules<Rules[]>({ subject: 'common', apiOptions: { anonymous: true } });
  }

  /**
   * 顔認証サービスへログインする
   * * アクセストークン利用のための必須制御
   */
  private loginService() {
    return this.post<IKPASLogin>(
      '/face-api/login',
      {
        tenantId: this.envFaceDetection.tenantId,
        userId: this.envFaceDetection.userId,
        passwd: this.envFaceDetection.password,
      }
    ).pipe(
      tap(login => this.login = login)
    );
  }

  /**
   * リクエストヘッダを作成する
   * @param isForm フォームデータ送信か否か
   */
  private createHeaders(isForm = false) {
    const headers = new HttpHeaders({ 'Accept': '*/*' });
    if (isForm) {
      headers.append('Content-Type', 'multipart/form-data');
      headers.append('enctype', 'multipart/form-data');
    }

    return headers;
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/face-detection-modal/face-detection-modal.service.ts